import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'

interface Layer {
  id: string
  enabled: boolean
  visible?: boolean
}

interface UseBaseLayerVisibilityProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  layers: Layer[]
  currentStyle: string
  mapStyle: string
  styleLoadedCount: number
}

/**
 * Hook to handle base layer visibility changes (streets, labels, terrain, contours, etc.)
 */
export const useBaseLayerVisibility = ({
  map,
  mapLoaded,
  layers,
  currentStyle,
  mapStyle,
  styleLoadedCount,
}: UseBaseLayerVisibilityProps) => {
  // Handle layer visibility changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Detect if we're using a Mapbox Standard style
    const isStandardStyle =
      currentStyle.includes('standard') || mapStyle.includes('standard')

    layers.forEach((layer) => {
      const { id, visible, enabled } = layer
      const isVisible = visible ?? enabled
      const visibility = isVisible ? 'visible' : 'none'

      try {
        // Property layers (work with both Standard and Legacy styles)
        if (id === 'building-3d') {
          // Control 3D buildings
          if (isVisible) {
            // Enable 3D buildings by adjusting pitch
            map.current.easeTo({ pitch: 60, duration: 1000 })
          } else {
            // Disable 3D buildings by resetting pitch
            map.current.easeTo({ pitch: 0, duration: 1000 })
          }
        } else if (id === 'parcel-boundaries') {
          // Parcel boundaries are handled by useParcelLayers hook
          // This is just a placeholder for the layer toggle
        } else if (id === 'land-dimension-metrics') {
          // Land dimension metrics are handled by useParcelLayers hook
          // This is just a placeholder for the layer toggle
        } else if (id === 'building-footprints') {
          // Building footprints are handled by useBuildingLayers hook
          // This is just a placeholder for the layer toggle
        } else if (id === 'building-dimension-metrics') {
          // Building dimension metrics are handled by useBuildingLayers hook
          // This is just a placeholder for the layer toggle
        } else if (isStandardStyle) {
          // Use setConfigProperty for Mapbox Standard styles
          if (id === 'labels') {
            // Control road and POI labels
            map.current.setConfigProperty(
              'basemap',
              'showRoadLabels',
              isVisible
            )
            map.current.setConfigProperty(
              'basemap',
              'showPointOfInterestLabels',
              isVisible
            )
          } else if (id === 'terrain') {
            // Control 3D buildings and terrain
            map.current.setConfigProperty('basemap', 'show3dObjects', isVisible)
          }
          // Note: Standard styles don't support toggling streets independently
          // Streets are always part of the basemap
        } else {
          // Use setLayoutProperty for legacy Mapbox styles
          if (id === 'streets') {
            // Control road/street layers
            const streetLayerPatterns = ['road', 'street', 'tunnel', 'bridge']
            try {
              const style = map.current.getStyle()
              if (style && style.layers) {
                style.layers.forEach((styleLayer: any) => {
                  if (
                    streetLayerPatterns.some((pattern) =>
                      styleLayer.id.includes(pattern)
                    ) &&
                    !styleLayer.id.includes('label') &&
                    styleLayer.type !== 'symbol'
                  ) {
                    map.current?.setLayoutProperty(
                      styleLayer.id,
                      'visibility',
                      visibility
                    )
                  }
                })
              }
            } catch (error) {
              // Style not loaded yet, skip
            }
          } else if (id === 'labels') {
            // Control label layers
            try {
              const style = map.current.getStyle()
              if (style && style.layers) {
                style.layers.forEach((styleLayer: any) => {
                  if (
                    styleLayer.id.includes('label') ||
                    styleLayer.type === 'symbol'
                  ) {
                    map.current?.setLayoutProperty(
                      styleLayer.id,
                      'visibility',
                      visibility
                    )
                  }
                })
              }
            } catch (error) {
              // Style not loaded yet, skip
            }
          } else if (id === 'terrain') {
            // Control 3D terrain
            if (isVisible) {
              // Check if terrain source already exists
              if (!map.current.getSource('mapbox-dem-terrain')) {
                map.current.addSource('mapbox-dem-terrain', {
                  type: 'raster-dem',
                  url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                })
              }
              map.current.setTerrain({
                source: 'mapbox-dem-terrain',
                exaggeration: 1.5,
              })
            } else {
              map.current.setTerrain(null)
            }
          } else if (id === 'contours') {
            // Control contour lines
            if (isVisible) {
              // Add contour source if it doesn't exist
              if (!map.current.getSource('mapbox-contours')) {
                map.current.addSource('mapbox-contours', {
                  type: 'vector',
                  url: 'mapbox://mapbox.mapbox-terrain-v2',
                })
              }

              // Add contour layer if it doesn't exist
              if (!map.current.getLayer('contour-lines')) {
                map.current.addLayer({
                  id: 'contour-lines',
                  type: 'line',
                  source: 'mapbox-contours',
                  'source-layer': 'contour',
                  layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                  },
                  paint: {
                    'line-color': '#877b59',
                    'line-width': 1,
                    'line-opacity': 0.5,
                  },
                })
              } else {
                // If layer exists, just make it visible
                map.current.setLayoutProperty(
                  'contour-lines',
                  'visibility',
                  'visible'
                )
              }
            } else {
              // Hide contour layer
              if (map.current.getLayer('contour-lines')) {
                map.current.setLayoutProperty(
                  'contour-lines',
                  'visibility',
                  'none'
                )
              }
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to toggle layer ${id}:`, error)
      }
    })
  }, [layers, mapLoaded, styleLoadedCount])
}
